/**
 * Whether a sign-in in Prest0n's browser lasts, in words (sign in once, S4, 2026-10-04; D9
 * `module:browserSignInDurabilityCopy`, class `state`).
 *
 * The state comes from the server's one rule (functions/src/prest0nSession/signinDurability.ts), carried on the
 * Integrations page's list of browser sign-ins. This table only words it. State words about the person's own
 * sign-in, never a sentence in Prest0n's voice; "Prest0n" keeps its capital P. `{minutes}` is a whole number.
 */

/** The server's closed list (held equal to signinDurability.ts by a test). */
export const SIGNIN_DURABILITY_STATES = [
  'kept',
  'kept-by-prest0n',
  'each-time',
  'not-confirmed',
  'needs-username',
  'password-not-accepted',
  'ended',
  'lost-in-reset',
  'unfinished',
] as const;
export type SigninDurabilityState = (typeof SIGNIN_DURABILITY_STATES)[number];

export interface SigninDurability {
  state: SigninDurabilityState;
  minutes?: number;
}

export type DurabilityLang = 'en' | 'es';

interface DurabilityWords {
  /** The line. */
  line: string;
  /** For `each-time`, the line when the minutes are known. */
  withMinutes?: string;
}

export const SIGNIN_DURABILITY_COPY: Record<DurabilityLang, Record<SigninDurabilityState, DurabilityWords>> = {
  en: {
    kept: { line: 'stays signed in' },
    'kept-by-prest0n': { line: 'stays signed in — Prest0n signs back in by itself' },
    'each-time': {
      line: 'signs out soon after its window closes',
      withMinutes: 'signs out about {minutes} minutes after its window is last used',
    },
    'not-confirmed': { line: 'not yet confirmed to stay signed in after its window closes' },
    'needs-username': { line: 'add your username so Prest0n can sign back in by itself' },
    'password-not-accepted': { line: 'the saved password was not accepted — sign in again' },
    ended: { line: 'signed out — sign in again' },
    'lost-in-reset': { line: 'signed out after a reset — sign in again' },
    unfinished: { line: 'sign-in not finished' },
  },
  es: {
    kept: { line: 'la sesión se mantiene' },
    'kept-by-prest0n': { line: 'la sesión se mantiene — Prest0n vuelve a iniciarla por sí mismo' },
    'each-time': {
      line: 'la sesión se cierra poco después de cerrar su ventana',
      withMinutes: 'la sesión se cierra unos {minutes} minutos después del último uso de su ventana',
    },
    'not-confirmed': { line: 'aún no se confirma que la sesión se mantenga al cerrar su ventana' },
    'needs-username': { line: 'agrega tu nombre de usuario para que Prest0n vuelva a iniciar sesión por sí mismo' },
    'password-not-accepted': { line: 'no se aceptó la contraseña guardada — vuelve a iniciar sesión' },
    ended: { line: 'sesión cerrada — vuelve a iniciar sesión' },
    'lost-in-reset': { line: 'sesión cerrada tras un reinicio — vuelve a iniciar sesión' },
    unfinished: { line: 'inicio de sesión sin terminar' },
  },
};

/** The server's state as it arrived, read closed: an unknown state, or anything malformed, is null. */
export function readSigninDurability(raw: unknown): SigninDurability | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { state?: unknown; minutes?: unknown };
  if (typeof r.state !== 'string' || !(SIGNIN_DURABILITY_STATES as readonly string[]).includes(r.state)) return null;
  const state = r.state as SigninDurabilityState;
  const minutes = typeof r.minutes === 'number' && Number.isInteger(r.minutes) && r.minutes > 0 && r.minutes <= 24 * 60 ? r.minutes : undefined;
  return minutes !== undefined && state === 'each-time' ? { state, minutes } : { state };
}

/** The line for a state ('' for none). */
export function signinDurabilityWords(raw: unknown, lang: DurabilityLang = 'en'): string {
  const d = readSigninDurability(raw);
  if (!d) return '';
  const words = SIGNIN_DURABILITY_COPY[lang === 'es' ? 'es' : 'en'][d.state];
  return d.minutes !== undefined && words.withMinutes ? words.withMinutes.replace('{minutes}', String(d.minutes)) : words.line;
}
